import React, { useEffect, useRef, useState } from 'react';
import {
  adminGetNotifications,
  adminGetMembers, adminUpdateMemberStatus, adminRenewMember, adminSetEditPermission,
  adminGetCases, adminGetCaseTimeline, adminUpdateCaseStatus,
  adminGetContent, adminCreateContent, adminUpdateContent, adminDeleteContent, adminUploadContentImage,
  adminGetMessages, adminReplyToMember,
  adminGetAppointments, adminUpdateAppointmentStatus,
  adminGetComplaints, adminUpdateComplaintStatus,
  adminGetVolunteers, adminUpdateVolunteerStatus,
  adminGetDocuments, adminUploadDocument, adminDownloadDocument,
} from '../../services/api';

const MEMBER_STATUSES = ['PENDING', 'ACTIVE', 'EXPIRED', 'SUSPENDED'];
const CASE_STATUSES = [
  'CASE_RECEIVED', 'CASE_REVIEW_COMPLETED', 'DOCUMENTS_PREPARED',
  'SUBMITTED', 'AWAITING_OUTCOME', 'FINALISED',
];
const APPOINTMENT_STATUSES = ['PENDING', 'CONFIRMED', 'CANCELLED', 'COMPLETED'];
const APPLICATION_STATUSES = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED'];
const CONTENT_CATEGORIES = [
  { value: 'NEWS', label: 'News & Announcements' },
  { value: 'PODCAST', label: 'Podcast Episode' },
  { value: 'RIGHTS_LIBRARY', label: "Rights Library Article" },
  { value: 'CASE_STORY', label: 'Real Case Story' },
];
const emptyContentForm = { title: '', content: '', category: 'NEWS', mediaUrl: '' };

const TABS = [
  { key: 'members', label: 'Members' },
  { key: 'cases', label: 'Cases' },
  { key: 'documents', label: 'Documents' },
  { key: 'messages', label: 'Messages' },
  { key: 'appointments', label: 'Appointments' },
  { key: 'complaints', label: 'Employer Complaints' },
  { key: 'volunteers', label: 'Volunteers' },
  { key: 'content', label: 'News & Podcast' },
];

function Badge({ count }) {
  if (!count) return null;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 20, height: 20, padding: '0 6px', marginLeft: 8,
        background: 'var(--color-gold)', color: 'var(--color-black)',
        borderRadius: 100, fontSize: '0.72rem', fontWeight: 700,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState('members');

  const [members, setMembers] = useState([]);
  const [cases, setCases] = useState([]);
  const [content, setContent] = useState([]);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [notifications, setNotifications] = useState({
    messages: 0, appointments: 0, complaints: 0, volunteers: 0, members: 0, cases: 0, documents: 0,
  });

  const [memberSearch, setMemberSearch] = useState('');
  const [caseSearch, setCaseSearch] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');

  const [contentForm, setContentForm] = useState(emptyContentForm);
  const [editingId, setEditingId] = useState(null);
  const [savingContent, setSavingContent] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [renewingId, setRenewingId] = useState(null);

  const [selectedMember, setSelectedMember] = useState(null);
  const [togglingPermission, setTogglingPermission] = useState(false);

  const [selectedCase, setSelectedCase] = useState(null);
  const [caseTimeline, setCaseTimeline] = useState([]);
  const [caseNote, setCaseNote] = useState('');
  const [updatingCaseStatus, setUpdatingCaseStatus] = useState(false);

  const [replyMemberId, setReplyMemberId] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const replyTextareaRef = useRef(null);

  const [uploadMemberId, setUploadMemberId] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadMembers = () => adminGetMembers().then((res) => setMembers(res.data)).catch(() => {});
  const loadCases = () => adminGetCases().then((res) => setCases(res.data)).catch(() => {});
  const loadContent = () => adminGetContent().then((res) => setContent(res.data)).catch(() => {});
  const loadMessages = () => adminGetMessages().then((res) => setMessages(res.data)).catch(() => {});
  const loadAppointments = () => adminGetAppointments().then((res) => setAppointments(res.data)).catch(() => {});
  const loadComplaints = () => adminGetComplaints().then((res) => setComplaints(res.data)).catch(() => {});
  const loadVolunteers = () => adminGetVolunteers().then((res) => setVolunteers(res.data)).catch(() => {});
  const loadDocuments = () => adminGetDocuments().then((res) => setDocuments(res.data)).catch(() => {});

  const refreshNotifications = () => {
    adminGetNotifications().then((res) => setNotifications(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadMembers();
    loadCases();
    loadContent();
    loadMessages();
    loadAppointments();
    loadComplaints();
    loadVolunteers();
    loadDocuments();
  }, []);

  // Poll the aggregated notification counts independently so badges stay
  // accurate even before the admin opens the relevant tab. Messages,
  // Documents, and (via server-side status) Members/Cases/Appointments/
  // Complaints/Volunteers all clear automatically once viewed/actioned —
  // see the comments on each handler below for how each one clears.
  useEffect(() => {
    refreshNotifications();
    const interval = setInterval(refreshNotifications, 20000);
    return () => clearInterval(interval);
  }, []);

  // Opening the Messages or Documents tab marks those items as seen
  // server-side (handled inside loadMessages/loadDocuments's backend
  // endpoints) — refresh the badge counts right after so the UI reflects
  // that immediately instead of waiting for the next poll.
  useEffect(() => {
    if (tab === 'messages' || tab === 'documents') {
      const t = setTimeout(refreshNotifications, 300);
      return () => clearTimeout(t);
    }
  }, [tab]);

  const filteredMembers = members.filter((m) => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.fullName || '').toLowerCase().includes(q) ||
      (m.membershipNumber || '').toLowerCase().includes(q) ||
      (m.province || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.phoneNumber || '').toLowerCase().includes(q)
    );
  });

  const filteredCases = cases.filter((c) => {
    const q = caseSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.caseNumber || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.memberFullName || '').toLowerCase().includes(q) ||
      (c.status || '').toLowerCase().includes(q)
    );
  });

  const filteredDocuments = documents.filter((d) => {
    const q = documentSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (d.memberFullName || '').toLowerCase().includes(q) ||
      (d.membershipNumber || '').toLowerCase().includes(q) ||
      (d.fileName || '').toLowerCase().includes(q) ||
      (d.caseNumber || '').toLowerCase().includes(q)
    );
  });

  // Member status changes badge only when moving OUT of PENDING (approving/
  // rejecting a new registration) — refresh so that badge updates promptly.
  const handleMemberStatusChange = async (id, status) => {
    await adminUpdateMemberStatus(id, status);
    loadMembers();
    refreshNotifications();
  };

  const handleRenew = async (id) => {
    setRenewingId(id);
    try {
      await adminRenewMember(id);
      loadMembers();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not renew this membership.');
    } finally {
      setRenewingId(null);
    }
  };

  const handleToggleEditPermission = async () => {
    if (!selectedMember) return;
    setTogglingPermission(true);
    try {
      const res = await adminSetEditPermission(selectedMember.id, !selectedMember.editAllowed);
      setSelectedMember(res.data);
      loadMembers();
    } catch (err) {
      alert('Could not update edit permission.');
    } finally {
      setTogglingPermission(false);
    }
  };

  const openCaseDetail = async (c) => {
    setSelectedCase(c);
    setCaseNote('');
    try {
      const res = await adminGetCaseTimeline(c.id);
      setCaseTimeline(res.data);
    } catch (err) {
      setCaseTimeline([]);
    }
  };

  // Case status changes clear the "new case" badge (moves off CASE_RECEIVED)
  // — refresh immediately rather than waiting for the next poll.
  const handleCaseStatusUpdate = async (status) => {
    if (!selectedCase) return;
    setUpdatingCaseStatus(true);
    try {
      const res = await adminUpdateCaseStatus(selectedCase.id, { status, note: caseNote });
      setSelectedCase({ ...selectedCase, status: res.data.status });
      setCaseNote('');
      const timelineRes = await adminGetCaseTimeline(selectedCase.id);
      setCaseTimeline(timelineRes.data);
      loadCases();
      refreshNotifications();
    } catch (err) {
      alert('Could not update case status.');
    } finally {
      setUpdatingCaseStatus(false);
    }
  };

  const handleContentSubmit = async (e) => {
    e.preventDefault();
    setSavingContent(true);
    try {
      if (editingId) {
        await adminUpdateContent(editingId, contentForm);
      } else {
        await adminCreateContent(contentForm);
      }
      setContentForm(emptyContentForm);
      setEditingId(null);
      loadContent();
    } finally {
      setSavingContent(false);
    }
  };

  const handleEditContent = (post) => {
    setEditingId(post.id);
    setContentForm({
      title: post.title || '',
      content: post.content || '',
      category: post.category || 'NEWS',
      mediaUrl: post.mediaUrl || '',
    });
    setImageFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageUpload = async () => {
    if (!editingId || !imageFile) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      await adminUploadContentImage(editingId, formData);
      setImageFile(null);
      loadContent();
    } catch (err) {
      alert('Could not upload image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    await adminDeleteContent(id);
    if (editingId === id) {
      setEditingId(null);
      setContentForm(emptyContentForm);
    }
    loadContent();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setContentForm(emptyContentForm);
    setImageFile(null);
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMemberId || !replyText.trim()) return;
    setSendingReply(true);
    try {
      await adminReplyToMember(replyMemberId, replyText);
      setReplyText('');
      loadMessages();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not send the reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  const handleQuickReply = (memberId) => {
    setReplyMemberId(String(memberId));
    replyTextareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    replyTextareaRef.current?.focus();
  };

  const handleAppointmentStatusChange = async (id, status) => {
    await adminUpdateAppointmentStatus(id, status);
    loadAppointments();
    refreshNotifications();
  };

  const handleComplaintStatusChange = async (id, status) => {
    await adminUpdateComplaintStatus(id, status);
    loadComplaints();
    refreshNotifications();
  };

  const handleVolunteerStatusChange = async (id, status) => {
    await adminUpdateVolunteerStatus(id, status);
    loadVolunteers();
    refreshNotifications();
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadMemberId || !uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      await adminUploadDocument(uploadMemberId, formData);
      setUploadFile(null);
      setUploadMemberId('');
      loadDocuments();
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await adminDownloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download this file.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? 'active' : ''}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'members' && <Badge count={notifications.members} />}
            {t.key === 'cases' && <Badge count={notifications.cases} />}
            {t.key === 'documents' && <Badge count={notifications.documents} />}
            {t.key === 'messages' && <Badge count={notifications.messages} />}
            {t.key === 'appointments' && <Badge count={notifications.appointments} />}
            {t.key === 'complaints' && <Badge count={notifications.complaints} />}
            {t.key === 'volunteers' && <Badge count={notifications.volunteers} />}
          </button>
        ))}
      </aside>

      <div className="dashboard-main">
        <h2 style={{ marginBottom: 24 }}>Administration Dashboard</h2>

        {tab === 'members' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <div className="form-group" style={{ maxWidth: 360 }}>
              <input
                placeholder="Search by name, number, province, email, phone..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: 10 }}>Membership #</th>
                  <th style={{ padding: 10 }}>Full Name</th>
                  <th style={{ padding: 10 }}>Province</th>
                  <th style={{ padding: 10 }}>Expiry Date</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>Update Status</th>
                  <th style={{ padding: 10 }}>Renewal</th>
                  <th style={{ padding: 10 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((m) => (
                  <tr
                    key={m.id}
                    style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                    onClick={() => setSelectedMember(m)}
                  >
                    <td style={{ padding: 10 }}>{m.membershipNumber}</td>
                    <td style={{ padding: 10 }}>{m.fullName}</td>
                    <td style={{ padding: 10 }}>{m.province}</td>
                    <td style={{ padding: 10 }}>{m.expiryDate || '—'}</td>
                    <td style={{ padding: 10 }}><span className="badge badge-pending">{m.status}</span></td>
                    <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                      <select defaultValue={m.status} onChange={(e) => handleMemberStatusChange(m.id, e.target.value)}>
                        {MEMBER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-outline-dark"
                        style={{ padding: '6px 14px' }}
                        onClick={() => handleRenew(m.id)}
                        disabled={renewingId === m.id}
                      >
                        {renewingId === m.id ? 'Renewing...' : 'Renew'}
                      </button>
                    </td>
                    <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-outline-dark" style={{ padding: '6px 14px' }} onClick={() => setSelectedMember(m)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredMembers.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No members found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {selectedMember && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setSelectedMember(null)}
          >
            <div className="card" style={{ maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>{selectedMember.fullName}</h3>
                <button type="button" onClick={() => setSelectedMember(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div className="grid grid-2" style={{ gap: 12, marginBottom: 20 }}>
                <div><div className="stat-label">Membership Number</div><p>{selectedMember.membershipNumber}</p></div>
                <div><div className="stat-label">Status</div><p><span className="badge badge-pending">{selectedMember.status}</span></p></div>
                <div><div className="stat-label">ID Number</div><p>{selectedMember.idNumber}</p></div>
                <div><div className="stat-label">Date of Birth</div><p>{selectedMember.dateOfBirth || '—'}</p></div>
                <div><div className="stat-label">Gender</div><p>{selectedMember.gender || '—'}</p></div>
                <div><div className="stat-label">Email</div><p>{selectedMember.email}</p></div>
                <div><div className="stat-label">Phone Number</div><p>{selectedMember.phoneNumber}</p></div>
                <div><div className="stat-label">Province</div><p>{selectedMember.province}</p></div>
                <div style={{ gridColumn: '1 / -1' }}><div className="stat-label">Residential Address</div><p>{selectedMember.residentialAddress || '—'}</p></div>
                <div><div className="stat-label">Occupation</div><p>{selectedMember.occupation || '—'}</p></div>
                <div><div className="stat-label">Employer</div><p>{selectedMember.employer || '—'}</p></div>
                <div><div className="stat-label">Membership Package</div><p>{selectedMember.packageName || '—'}</p></div>
                <div><div className="stat-label">Package Price</div><p>{selectedMember.packagePrice || '—'}</p></div>
                <div><div className="stat-label">Joining Date</div><p>{selectedMember.joiningDate || '—'}</p></div>
                <div><div className="stat-label">Expiry Date</div><p>{selectedMember.expiryDate || '—'}</p></div>
                <div><div className="stat-label">Referral Code</div><p>{selectedMember.referralCode || '—'}</p></div>
              </div>
              <div style={{ borderTop: '1px solid #e7e3d8', paddingTop: 16 }}>
                <p style={{ marginBottom: 10 }}>
                  <strong>Profile edit permission:</strong>{' '}
                  {selectedMember.editAllowed
                    ? 'This member is currently allowed to edit their own details. Permission will automatically lock again once they save an edit.'
                    : 'This member cannot currently edit their own details.'}
                </p>
                <button className="btn btn-gold" onClick={handleToggleEditPermission} disabled={togglingPermission}>
                  {togglingPermission ? 'Updating...' : selectedMember.editAllowed ? 'Revoke Edit Permission' : 'Grant Edit Permission'}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'cases' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <div className="form-group" style={{ maxWidth: 360 }}>
              <input
                placeholder="Search by case #, title, member, status..."
                value={caseSearch}
                onChange={(e) => setCaseSearch(e.target.value)}
              />
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: 10 }}>Case #</th>
                  <th style={{ padding: 10 }}>Title</th>
                  <th style={{ padding: 10 }}>Member</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>Update Status</th>
                  <th style={{ padding: 10 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => openCaseDetail(c)}>
                    <td style={{ padding: 10 }}>{c.caseNumber}</td>
                    <td style={{ padding: 10 }}>{c.title}</td>
                    <td style={{ padding: 10 }}>{c.memberFullName}</td>
                    <td style={{ padding: 10 }}><span className="badge badge-pending">{c.status.replace(/_/g, ' ')}</span></td>
                    <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                      <select
                        defaultValue={c.status}
                        onChange={async (e) => {
                          await adminUpdateCaseStatus(c.id, { status: e.target.value });
                          loadCases();
                          refreshNotifications();
                        }}
                      >
                        {CASE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-outline-dark" style={{ padding: '6px 14px' }} onClick={() => openCaseDetail(c)}>View</button>
                    </td>
                  </tr>
                ))}
                {filteredCases.length === 0 && (
                  <tr><td colSpan="6" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No cases found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {selectedCase && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setSelectedCase(null)}
          >
            <div className="card" style={{ maxWidth: 600, width: '90%', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0 }}>{selectedCase.title}</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-grey)', margin: '4px 0 0' }}>{selectedCase.caseNumber}</p>
                </div>
                <button type="button" onClick={() => setSelectedCase(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>

              <div className="grid grid-2" style={{ gap: 12, marginBottom: 16 }}>
                <div><div className="stat-label">Member</div><p>{selectedCase.memberFullName} ({selectedCase.membershipNumber})</p></div>
                <div><div className="stat-label">Phone</div><p>{selectedCase.memberPhoneNumber || '—'}</p></div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div className="stat-label">Member's Message</div>
                <p style={{ background: '#f7f5ef', padding: 12, borderRadius: 4, marginTop: 6 }}>
                  {selectedCase.description || 'No description provided.'}
                </p>
              </div>

              <div style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 10 }}>Progress Timeline</h4>
                {caseTimeline.length === 0 ? (
                  <p style={{ fontSize: '0.9rem', color: 'var(--color-grey)' }}>No status changes recorded yet.</p>
                ) : (
                  <div className="timeline">
                    {caseTimeline.map((u, i) => (
                      <div className="timeline-step" key={u.id}>
                        <div className="timeline-marker">
                          <div className="timeline-dot done" />
                          {i < caseTimeline.length - 1 && <div className="timeline-line done" />}
                        </div>
                        <div className="timeline-content">
                          <h4>{u.status.replace(/_/g, ' ')}</h4>
                          <p style={{ margin: 0, fontSize: '0.85rem' }}>{new Date(u.createdAt).toLocaleString()}</p>
                          {u.note && <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>{u.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ borderTop: '1px solid #e7e3d8', paddingTop: 16 }}>
                <h4 style={{ marginBottom: 10 }}>Update Status</h4>
                <div className="form-group">
                  <label>Note (optional)</label>
                  <textarea rows="2" value={caseNote} onChange={(e) => setCaseNote(e.target.value)} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CASE_STATUSES.map((s) => (
                    <button
                      key={s}
                      className={s === selectedCase.status ? 'btn btn-gold' : 'btn btn-outline-dark'}
                      style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                      onClick={() => handleCaseStatusUpdate(s)}
                      disabled={updatingCaseStatus}
                    >
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'documents' && (
          <div>
            <div className="card" style={{ marginBottom: 24, maxWidth: 560 }}>
              <h3>Upload a Document for a Member</h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-grey)', marginBottom: 16 }}>
                Send a member a completed document — a finalised letter, CCMA form, or other paperwork.
              </p>
              <form onSubmit={handleDocumentUpload}>
                <div className="form-group">
                  <label>Member</label>
                  <select required value={uploadMemberId} onChange={(e) => setUploadMemberId(e.target.value)}>
                    <option value="">Select a member</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.fullName} ({m.membershipNumber})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>File</label>
                  <input type="file" required onChange={(e) => setUploadFile(e.target.files[0])} />
                </div>
                <button className="btn btn-gold" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload Document'}</button>
              </form>
            </div>

            <div className="card" style={{ overflowX: 'auto' }}>
              <div className="form-group" style={{ maxWidth: 360 }}>
                <input
                  placeholder="Search by member, filename, case #..."
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                />
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: 10 }}>Member</th>
                    <th style={{ padding: 10 }}>File</th>
                    <th style={{ padding: 10 }}>Case</th>
                    <th style={{ padding: 10 }}>Uploaded By</th>
                    <th style={{ padding: 10 }}>Date</th>
                    <th style={{ padding: 10 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((d) => (
                    <tr key={d.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10 }}>{d.memberFullName} ({d.membershipNumber})</td>
                      <td style={{ padding: 10 }}>{d.fileName}</td>
                      <td style={{ padding: 10 }}>{d.caseNumber || '—'}</td>
                      <td style={{ padding: 10 }}><span className="badge badge-pending">{d.uploadedBy === 'MEMBER' ? 'Member' : 'Organisation'}</span></td>
                      <td style={{ padding: 10 }}>{new Date(d.uploadedAt).toLocaleDateString()}</td>
                      <td style={{ padding: 10 }}>
                        <button className="btn btn-outline-dark" style={{ padding: '6px 14px' }} onClick={() => handleDownload(d)} disabled={downloadingId === d.id}>
                          {downloadingId === d.id ? 'Downloading...' : 'Download'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredDocuments.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No documents found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <div>
            <div className="card" style={{ marginBottom: 24, maxWidth: 560 }}>
              <h3>Reply to a Member</h3>
              <form onSubmit={handleReplySubmit}>
                <div className="form-group">
                  <label>Member</label>
                  <select required value={replyMemberId} onChange={(e) => setReplyMemberId(e.target.value)}>
                    <option value="">Select a member</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.fullName} ({m.membershipNumber})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea ref={replyTextareaRef} rows="3" required value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                </div>
                <button className="btn btn-gold" disabled={sendingReply}>{sendingReply ? 'Sending...' : 'Send Reply'}</button>
              </form>
            </div>

            <div className="card" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: 10 }}>From</th>
                    <th style={{ padding: 10 }}>Message</th>
                    <th style={{ padding: 10 }}>Date</th>
                    <th style={{ padding: 10 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((msg) => (
                    <tr key={msg.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10 }}>{msg.sender === 'MEMBER' ? `${msg.memberFullName} (${msg.membershipNumber})` : 'You (Organisation)'}</td>
                      <td style={{ padding: 10 }}>{msg.content}</td>
                      <td style={{ padding: 10 }}>{new Date(msg.createdAt).toLocaleString()}</td>
                      <td style={{ padding: 10 }}>
                        {msg.sender === 'MEMBER' && (
                          <button className="btn btn-outline-dark" style={{ padding: '6px 14px' }} onClick={() => handleQuickReply(msg.memberId)}>Reply</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {messages.length === 0 && (
                    <tr><td colSpan="4" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No messages yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'appointments' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: 10 }}>Member</th>
                  <th style={{ padding: 10 }}>Type</th>
                  <th style={{ padding: 10 }}>Date/Time</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>Update Status</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 10 }}>{a.memberFullName} ({a.membershipNumber})</td>
                    <td style={{ padding: 10 }}>{a.type}</td>
                    <td style={{ padding: 10 }}>{new Date(a.requestedDateTime).toLocaleString()}</td>
                    <td style={{ padding: 10 }}><span className="badge badge-pending">{a.status}</span></td>
                    <td style={{ padding: 10 }}>
                      <select defaultValue={a.status} onChange={(e) => handleAppointmentStatusChange(a.id, e.target.value)}>
                        {APPOINTMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {appointments.length === 0 && (
                  <tr><td colSpan="5" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No appointments booked yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'complaints' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: 10 }}>Name</th>
                  <th style={{ padding: 10 }}>Employer</th>
                  <th style={{ padding: 10 }}>Description</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>Update Status</th>
                </tr>
              </thead>
              <tbody>
                {complaints.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 10 }}>{c.complainantName}<br /><span style={{ fontSize: '0.8rem', color: 'var(--color-grey)' }}>{c.complainantPhone}</span></td>
                    <td style={{ padding: 10 }}>{c.employerName}</td>
                    <td style={{ padding: 10, maxWidth: 300 }}>{c.problemDescription}</td>
                    <td style={{ padding: 10 }}><span className="badge badge-pending">{c.status}</span></td>
                    <td style={{ padding: 10 }}>
                      <select defaultValue={c.status} onChange={(e) => handleComplaintStatusChange(c.id, e.target.value)}>
                        {APPLICATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {complaints.length === 0 && (
                  <tr><td colSpan="5" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No complaints submitted yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'volunteers' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: 10 }}>Name</th>
                  <th style={{ padding: 10 }}>Province</th>
                  <th style={{ padding: 10 }}>Contact</th>
                  <th style={{ padding: 10 }}>Status</th>
                  <th style={{ padding: 10 }}>Update Status</th>
                </tr>
              </thead>
              <tbody>
                {volunteers.map((v) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 10 }}>{v.fullName}</td>
                    <td style={{ padding: 10 }}>{v.province}</td>
                    <td style={{ padding: 10 }}>{v.email}<br /><span style={{ fontSize: '0.8rem', color: 'var(--color-grey)' }}>{v.phoneNumber}</span></td>
                    <td style={{ padding: 10 }}><span className="badge badge-pending">{v.status}</span></td>
                    <td style={{ padding: 10 }}>
                      <select defaultValue={v.status} onChange={(e) => handleVolunteerStatusChange(v.id, e.target.value)}>
                        {APPLICATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {volunteers.length === 0 && (
                  <tr><td colSpan="5" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No volunteer applications yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'content' && (
          <div>
            <div className="card" style={{ marginBottom: 24, maxWidth: 640 }}>
              <h3>{editingId ? 'Edit Post' : 'Create New Post'}</h3>
              <form onSubmit={handleContentSubmit}>
                <div className="form-group">
                  <label>Category</label>
                  <select value={contentForm.category} onChange={(e) => setContentForm({ ...contentForm, category: e.target.value })}>
                    {CONTENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input required value={contentForm.title} onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Content / Description</label>
                  <textarea rows="5" value={contentForm.content} onChange={(e) => setContentForm({ ...contentForm, content: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>YouTube / Video URL {contentForm.category === 'PODCAST' ? '' : '(optional)'}</label>
                  <input
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    required={contentForm.category === 'PODCAST'}
                    value={contentForm.mediaUrl}
                    onChange={(e) => setContentForm({ ...contentForm, mediaUrl: e.target.value })}
                  />
                </div>

                {editingId && (
                  <div className="form-group">
                    <label>Featured Image</label>
                    {content.find((c) => c.id === editingId)?.imageUrl && (
                      <img src={content.find((c) => c.id === editingId).imageUrl} alt="Current featured" style={{ maxWidth: 220, borderRadius: 4, marginBottom: 10, display: 'block' }} />
                    )}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                      <button type="button" className="btn btn-outline-dark" style={{ padding: '8px 16px' }} onClick={handleImageUpload} disabled={!imageFile || uploadingImage}>
                        {uploadingImage ? 'Uploading...' : 'Upload Image'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', marginTop: 6 }}>Uploads immediately — no need to click Update Post afterward.</p>
                  </div>
                )}
                {!editingId && (
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-grey)', marginBottom: 18 }}>Publish the post first, then click Edit on it below to add a featured image.</p>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="btn btn-gold" disabled={savingContent}>{savingContent ? 'Saving...' : editingId ? 'Update Post' : 'Publish Post'}</button>
                  {editingId && <button type="button" className="btn btn-outline-dark" onClick={cancelEdit}>Cancel</button>}
                </div>
              </form>
            </div>

            <div className="card" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                    <th style={{ padding: 10 }}>Image</th>
                    <th style={{ padding: 10 }}>Category</th>
                    <th style={{ padding: 10 }}>Title</th>
                    <th style={{ padding: 10 }}>Video</th>
                    <th style={{ padding: 10 }}>Published</th>
                    <th style={{ padding: 10 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map((post) => (
                    <tr key={post.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 10 }}>
                        {post.imageUrl ? (
                          <img src={post.imageUrl} alt={post.title} style={{ width: 60, height: 44, objectFit: 'cover', borderRadius: 4 }} />
                        ) : <span style={{ color: 'var(--color-grey)', fontSize: '0.8rem' }}>None</span>}
                      </td>
                      <td style={{ padding: 10 }}><span className="badge badge-pending">{post.category.replace(/_/g, ' ')}</span></td>
                      <td style={{ padding: 10 }}>{post.title}</td>
                      <td style={{ padding: 10 }}>
                        {post.mediaUrl ? <a href={post.mediaUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-gold)' }}>View</a> : '—'}
                      </td>
                      <td style={{ padding: 10 }}>{new Date(post.publishedAt).toLocaleDateString()}</td>
                      <td style={{ padding: 10, display: 'flex', gap: 10 }}>
                        <button className="btn btn-outline-dark" style={{ padding: '6px 14px' }} onClick={() => handleEditContent(post)}>Edit</button>
                        <button className="btn" style={{ padding: '6px 14px', background: '#f8d9d9', color: '#a11f1f', clipPath: 'none' }} onClick={() => handleDeleteContent(post.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                  {content.length === 0 && (
                    <tr><td colSpan="6" style={{ padding: 16, textAlign: 'center', color: 'var(--color-grey)' }}>No posts yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
